import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, UserX } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Exclusions = () => {
  const [exclusions, setExclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ email: '', reason: '' });

  useEffect(() => {
    fetchExclusions();
  }, []);

  const fetchExclusions = async () => {
    try {
      const response = await axios.get(`${API}/exclusions`);
      setExclusions(response.data);
    } catch (error) {
      console.error('Error fetching exclusions:', error);
      toast.error('Error al cargar la lista de exclusión');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExclusion = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.post(`${API}/exclusions`, formData);
      toast.success('Email añadido a la lista de exclusión');
      setFormData({ email: '', reason: '' });
      fetchExclusions();
    } catch (error) {
      console.error('Error adding exclusion:', error);
      toast.error(error?.response?.data?.detail || 'Error al añadir el email');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExclusion = async (id) => {
    if (!window.confirm('¿Eliminar este email de la lista de exclusión?')) return;
    try {
      await axios.delete(`${API}/exclusions/${id}`);
      toast.success('Email eliminado de la lista de exclusión');
      fetchExclusions();
    } catch (error) {
      console.error('Error deleting exclusion:', error);
      toast.error('Error al eliminar el email');
    }
  };

  return (
    <div className="space-y-6" data-testid="exclusions-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Lista de Exclusión</h1>
        <p className="mt-2 text-gray-600">Evita enviar campañas a destinatarios excluidos</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Agregar Email</CardTitle>
          <CardDescription>Añade un correo a la lista de exclusión</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddExclusion} className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-1">
              <Label htmlFor="exclusion-email">Email *</Label>
              <Input
                id="exclusion-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="exclusion-email-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="exclusion-reason">Motivo (opcional)</Label>
              <Textarea
                id="exclusion-reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={2}
                data-testid="exclusion-reason-input"
              />
            </div>
            <div className="md:col-span-3">
              <Button type="submit" disabled={saving} data-testid="add-exclusion-button">
                <UserX className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : 'Añadir a exclusión'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Emails Excluidos ({exclusions.length})</CardTitle>
          <CardDescription>Estos contactos no recibirán campañas</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando exclusiones...</div>
          ) : exclusions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No hay emails excluidos.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exclusions.map((entry) => (
                    <TableRow key={entry.id} data-testid={`exclusion-row-${entry.id}`}>
                      <TableCell data-testid={`exclusion-email-${entry.id}`}>{entry.email}</TableCell>
                      <TableCell data-testid={`exclusion-reason-${entry.id}`}>{entry.reason || '-'}</TableCell>
                      <TableCell data-testid={`exclusion-date-${entry.id}`}>
                        {entry.created_at ? new Date(entry.created_at).toLocaleDateString('es-ES') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteExclusion(entry.id)}
                          data-testid={`delete-exclusion-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Exclusions;